import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector, useDispatch } from "react-redux";
import "./stock-report.css";
import { getFamily } from "../Redux/Actions/IngredientActions";

const StockReportUser = () => {
  const dispatch = useDispatch();
  const [stockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({
    location: "",
    ingredient: "",
    search: ""
  });
  const [safetyStocks, setSafetyStocks] = useState({});
  const [editingSafetyStock, setEditingSafetyStock] = useState(null);
  const [editValue, setEditValue] = useState("");
  
  const familiesData = useSelector((state) => state.IngredientReducer?.families || []);
  const user = useSelector((state) => state.auth?.user);

  const canEditSafetyStock = user?.role === 'admin' || user?.role === 'useradmin';

  useEffect(() => {
    const fetchStockData = async () => {
      try {
        const response = await axios.get("/api/Invoices/stock-report");
        setStockData(response.data);
        setFilteredData(response.data);
        
        const safetyStocksMap = {};
        response.data.forEach(item => {
          if (item.ingredient && item.localisation) {
            const key = `${item.ingredient._id || item.ingredient}-${item.localisation._id || item.localisation}`;
            safetyStocksMap[key] = item.safetyStock || 0;
          }
        });
        setSafetyStocks(safetyStocksMap);
        
        setLoading(false);
      } catch (error) {
        console.error("Erreur récupération stock:", error);
        setLoading(false);
      }
    };

    const fetchLocations = async () => {
      try {
        const response = await axios.get("/api/Locationspin/getAllLocations");
        let locationsData = response.data;
        
        if (response.data && Array.isArray(response.data)) {
          locationsData = response.data;
        } else if (response.data && response.data.locations) {
          locationsData = response.data.locations;
        } else if (response.data && response.data.data) {
          locationsData = response.data.data;
        }
        
        setLocations(locationsData);
      } catch (error) {
        console.error("Erreur chargement localisations:", error);
      }
    };

    fetchStockData();
    fetchLocations();
  }, []);

  useEffect(() => {
    if (familiesData.length === 0) {
      dispatch(getFamily());
    }
  }, [familiesData, dispatch]);

  const updateSafetyStock = async (ingredientId, locationId, newValue) => {
    try {
      await axios.put("/api/stock/update-safety-stock", {
        ingredient: ingredientId,
        localisation: locationId,
        safetyStock: parseFloat(newValue) || 0
      });
      
      setSafetyStocks(prev => ({
        ...prev,
        [`${ingredientId}-${locationId}`]: parseFloat(newValue) || 0
      }));
      
      const response = await axios.get("/api/Invoices/stock-report");
      setStockData(response.data);
      setFilteredData(response.data);
      
      setEditingSafetyStock(null);
    } catch (error) {
      console.error("Erreur mise à jour stock sécurité:", error);
      alert("Erreur lors de la mise à jour du stock de sécurité");
    }
  };

  // 🔥 FONCTION CORRIGÉE POUR LA COLORATION DES LIGNES
  const getRowClass = (currentQuantity, safetyStock) => {
    // Convertir en nombres pour comparaison
    const currentQty = parseFloat(currentQuantity);
    const safety = parseFloat(safetyStock);
    
    // Vérifier que les valeurs sont valides
    if (isNaN(currentQty) || isNaN(safety) || safety <= 0) {
      return "";
    }
    
    console.log("Vérification couleur:", { 
      currentQty, 
      safety, 
      condition1: currentQty <= safety, 
      condition2: currentQty <= safety * 2 
    });
    
    if (currentQty <= safety) {
      console.log("→ Ligne CRITIQUE (rouge)");
      return "stock-critical-row";
    } else if (currentQty <= safety * 2) {
      console.log("→ Ligne WARNING (orange)");
      return "stock-warning-row";
    }
    
    return "";
  };

  useEffect(() => {
    let filtered = [...stockData];

    if (filters.location) {
      filtered = filtered.filter(item => {
        const locationInfo = getLocationInfo(item.localisation);
        return locationInfo.name === filters.location;
      });
    }

    if (filters.ingredient) {
      filtered = filtered.filter(item => {
        const ingredientInfo = findIngredientInfo(item.ingredient);
        return ingredientInfo.name.toLowerCase().includes(filters.ingredient.toLowerCase());
      });
    }

    if (filters.search) {
      filtered = filtered.filter(item => {
        const ingredientInfo = findIngredientInfo(item.ingredient);
        const locationInfo = getLocationInfo(item.localisation);
        
        return (
          ingredientInfo.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          ingredientInfo.family.toLowerCase().includes(filters.search.toLowerCase()) ||
          locationInfo.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          item.quantityUnit.toLowerCase().includes(filters.search.toLowerCase())
        );
      });
    }

    setFilteredData(filtered);
  }, [filters, stockData]);

  const getLocationInfo = (locationData) => {
    if (!locationData) {
      return { name: "Non spécifiée", isValid: false };
    }
    
    if (locationData.name && !locationData.name.includes('Localisation-')) {
      return { name: locationData.name, isValid: true };
    }
    
    let locationId;
    if (locationData._id) {
      locationId = locationData._id.toString();
    } else if (typeof locationData === 'string') {
      locationId = locationData;
    }
    
    if (locationId) {
      const foundLocation = locations.find(loc => 
        loc._id && loc._id.toString() === locationId
      );
      
      if (foundLocation && foundLocation.name) {
        return { name: foundLocation.name, isValid: true };
      }
    }
    
    return { name: "Localisation inconnue", isValid: false };
  };

  const findIngredientInfo = (ingredient) => {
    if (!ingredient) {
      return { name: "Non associé", family: "N/A", isValid: false };
    }
    
    const ingredientId = ingredient._id || ingredient;
    const searchId = typeof ingredientId === 'string' ? ingredientId : ingredientId.toString();
    
    if (ingredient.name && ingredient.name !== `Ingrédient-${searchId.slice(-6)}`) {
      const familyName = findIngredientFamily(searchId);
      return { 
        name: ingredient.name, 
        family: familyName, 
        isValid: true 
      };
    }
    
    for (const family of familiesData) {
      if (family.ingredients && Array.isArray(family.ingredients)) {
        const foundIngredient = family.ingredients.find(
          ing => {
            const ingId = ing._id ? ing._id.toString() : null;
            return ingId === searchId;
          }
        );
        if (foundIngredient) {
          return {
            name: foundIngredient.name,
            family: family.name,
            isValid: true
          };
        }
      }
    }
    
    return {
      name: `Ingrédient-${searchId.slice(-6)}`,
      family: "Famille non trouvée", 
      isValid: false
    };
  };

  const findIngredientFamily = (ingredientId) => {
    if (!ingredientId) return "Famille inconnue";
    
    const searchId = typeof ingredientId === 'string' ? ingredientId : ingredientId.toString();
    
    for (const family of familiesData) {
      if (family.ingredients) {
        const foundIngredient = family.ingredients.find(
          ing => {
            const ingId = ing._id ? ing._id.toString() : null;
            return ingId === searchId;
          }
        );
        if (foundIngredient) {
          return family.name;
        }
      }
    }
    return "Famille non trouvée";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return "Date invalide";
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      location: "",
      ingredient: "",
      search: ""
    });
  };

  const uniqueIngredients = [...new Set(
    stockData
      .map(item => findIngredientInfo(item.ingredient).name)
      .filter(name => name && name !== "Non associé")
  )].sort();

  const uniqueLocations = [...new Set(
    stockData
      .map(item => getLocationInfo(item.localisation).name)
      .filter(name => name && name !== "Non spécifiée")
  )].sort();

  if (loading) {
    return (
      <div className="stock-container">
        <div className="loading-container">
          <span className="loading-icon">📦</span>
          <div className="loading-text">Chargement du stock...</div>
        </div>
      </div>
    );
  }

  const stats = {
    total: stockData.length,
    filtered: filteredData.length,
    withValidIngredient: stockData.filter(item => {
      if (!item.ingredient) return false;
      const info = findIngredientInfo(item.ingredient);
      return info.isValid;
    }).length,
    withValidLocation: stockData.filter(item => {
      const locationInfo = getLocationInfo(item.localisation);
      return locationInfo.isValid;
    }).length
  };

  const allDataValid = stats.withValidIngredient === stats.total && stats.withValidLocation === stats.total;
  const hasActiveFilters = filters.location || filters.ingredient || filters.search;

  return (
    <div className="stock-container">
      <div className="stock-header">
        <h1 className="stock-title">État du Stock</h1>
        <p className="stock-subtitle">Vue d'ensemble complète de votre inventaire</p>
        {canEditSafetyStock && (
          <div className="permission-notice">
            <span className="permission-icon">🔧</span>
            Vous pouvez modifier les stocks de sécurité (rôle: {user?.role})
          </div>
        )}
      </div>
      
      <div className="filters-container">
        <div className="filters-header">
          <h3 className="filters-title">Filtres</h3>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={clearFilters}>
              Effacer les filtres
            </button>
          )}
        </div>
        
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">Recherche</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Rechercher un ingrédient, famille, localisation..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Localisation</label>
            <select
              className="filter-select"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
            >
              <option value="">Toutes les localisations</option>
              {uniqueLocations.map(location => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Ingrédient</label>
            <select
              className="filter-select"
              value={filters.ingredient}
              onChange={(e) => handleFilterChange('ingredient', e.target.value)}
            >
              <option value="">Tous les ingrédients</option>
              {uniqueIngredients.map(ingredient => (
                <option key={ingredient} value={ingredient}>
                  {ingredient}
                </option>
              ))}
            </select>
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filter-indicator">
            <span className="filter-count">
              {filteredData.length} résultat(s) sur {stats.total}
            </span>
          </div>
        )}
      </div>
      
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Articles en Stock</div>
        </div>

        <div className="stat-card">
          <div className="stat-number" style={{ 
            color: stats.withValidIngredient === stats.total ? '#27ae60' : '#f39c12' 
          }}>
            {stats.withValidIngredient}
          </div>
          <div className="stat-label">Ingrédients Valides</div>
        </div>

        <div className="stat-card">
          <div className="stat-number" style={{ 
            color: stats.withValidLocation === stats.total ? '#27ae60' : '#f39c12' 
          }}>
            {stats.withValidLocation}
          </div>
          <div className="stat-label">Localisations Valides</div>
        </div>

        {hasActiveFilters && (
          <div className="stat-card filtered-stat">
            <div className="stat-number" style={{ color: '#667eea' }}>
              {filteredData.length}
            </div>
            <div className="stat-label">Résultats filtrés</div>
          </div>
        )}
      </div>

      <div className="stock-table-container">
        <table className="stock-table">
          <thead>
            <tr>
              <th data-tooltip="Famille de l'ingrédient">Famille</th>
              <th data-tooltip="Nom de l'ingrédient">Ingrédient</th>
              <th data-tooltip="Quantité disponible">Quantité</th>
              <th data-tooltip="Unité de mesure">Unité</th>
              <th data-tooltip="Prix moyen unitaire">Prix Unitaire</th>
              <th data-tooltip="Valeur totale du stock">Valeur</th>
              <th data-tooltip="Emplacement du stock">Localisation</th>
              <th data-tooltip="Date de la dernière mise à jour">Date d'entrée</th>
              <th data-tooltip="Stock minimum à maintenir">Stock de sécurité</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((item, index) => {
                const totalValue = (item.totalQuantity || 0) * (item.averagePrice || 0);
                const ingredientInfo = findIngredientInfo(item.ingredient);
                const locationInfo = getLocationInfo(item.localisation);
                const hasValidIngredient = ingredientInfo.isValid;
                const hasValidLocation = locationInfo.isValid;
                
                const ingredientId = item.ingredient?._id || item.ingredient;
                const locationId = item.localisation?._id || item.localisation;
                const safetyStockKey = `${ingredientId}-${locationId}`;
                const safetyStockValue = safetyStocks[safetyStockKey] || item.safetyStock || 0;
                
                // 🔥 UTILISATION DE LA FONCTION CORRIGÉE
                const rowClass = getRowClass(item.totalQuantity, safetyStockValue);
                const isRowValid = hasValidIngredient && hasValidLocation;
                // 🔥 AJOUTER LA CLASSE AU TR
                const finalRowClass = isRowValid ? rowClass : `invalid-row ${rowClass}`;
                
                return (
                  <tr key={item._id || index} className={finalRowClass}>
                    <td className="family-column">
                      {ingredientInfo.family}
                    </td>
                    <td className={hasValidIngredient ? "ingredient-column" : "ingredient-column ingredient-invalid"}>
                      {ingredientInfo.name}
                      {!hasValidIngredient && (
                        <div className="ingredient-id">
                          ID: {(item.ingredient?._id || item.ingredient)?.toString().slice(-8)}
                        </div>
                      )}
                    </td>
                    <td className="quantity-column">
                      {item.totalQuantity?.toFixed(2)}
                    </td>
                    <td className="unit-column">
                      {item.quantityUnit}
                    </td>
                    <td className="price-column">
                      {item.averagePrice?.toFixed(2)} DT
                    </td>
                    <td className="value-column">
                      {totalValue.toFixed(2)} DT
                    </td>
                    <td className={hasValidLocation ? "location-column" : "location-column location-invalid"}>
                      {locationInfo.name}
                    </td>
                    <td className="date-column">
                      {formatDate(item.lastUpdated)}
                    </td>
                    <td className="safety-stock-column">
                      {editingSafetyStock === safetyStockKey && canEditSafetyStock ? (
                        <div className="safety-stock-edit">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="safety-stock-input"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                updateSafetyStock(ingredientId, locationId, editValue);
                              }
                              if (e.key === 'Escape') {
                                setEditingSafetyStock(null);
                              }
                            }}
                          />
                          <button
                            onClick={() => updateSafetyStock(ingredientId, locationId, editValue)}
                            className="safety-stock-save-btn"
                            title="Enregistrer"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => setEditingSafetyStock(null)}
                            className="safety-stock-cancel-btn"
                            title="Annuler"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div 
                          className="safety-stock-display"
                          onClick={() => {
                            if (canEditSafetyStock) {
                              setEditingSafetyStock(safetyStockKey);
                              setEditValue(safetyStockValue.toString());
                            }
                          }}
                          style={{ cursor: canEditSafetyStock ? 'pointer' : 'default' }}
                          title={canEditSafetyStock ? "Cliquer pour modifier" : "Lecture seule"}
                        >
                          <span className="safety-stock-value">
                            {safetyStockValue.toFixed(2)}
                          </span>
                          {canEditSafetyStock && (
                            <span className="edit-icon" title="Modifier">✏️</span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="9" className="empty-state">
                  {hasActiveFilters ? 
                    "Aucun résultat trouvé avec les filtres actuels" : 
                    "Aucune donnée de stock disponible"
                  }
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="color-legend">
        <div className="legend-title">Légende des couleurs :</div>
        <div className="legend-items">
          <div className="legend-item">
            <div className="legend-color legend-critical"></div>
            <span>Stock ≤ Stock de sécurité (Risque de rupture)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color legend-warning"></div>
            <span>Stock ≤ 2 × Stock de sécurité (Attention)</span>
          </div>
          <div className="legend-item">
            <div className="legend-color legend-normal"></div>
            <span>Stock normal</span>
          </div>
          {!canEditSafetyStock && (
            <div className="legend-item">
              <span className="permission-note">⚠️ Seuls les administrateurs peuvent modifier les stocks de sécurité</span>
            </div>
          )}
        </div>
      </div>

      {allDataValid ? (
        <div className="info-message success">
          <strong>✅ Toutes les données sont valides et à jour</strong>
        </div>
      ) : (
        <div className="info-message">
          <div className="info-content">
            <span className="info-icon">💡</span>
            <div className="info-details">
              <strong>Information:</strong> Certaines données nécessitent une attention.
              {stats.withValidIngredient < stats.total && (
                <div>• {stats.total - stats.withValidIngredient} ingrédient(s) non reconnu(s)</div>
              )}
              {stats.withValidLocation < stats.total && (
                <div>• {stats.total - stats.withValidLocation} localisation(s) non reconnue(s)</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockReportUser;